import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Modal from '../../components/common/Modal';
import {
    ChevronLeftIcon,
    PlusIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    CheckIcon,
    XMarkIcon,
    GlobeAltIcon,
    HashtagIcon,
    Bars3BottomLeftIcon,
    NumberedListIcon
} from '@heroicons/react/24/outline';

const SyncToggle = ({ syncToAll, setSyncToAll, isVisible }) => {
    if (!isVisible) return null;

    return (
        <div className="col-span-2 mt-4 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <label
                className="flex items-center gap-4 p-5 bg-indigo-50/30 border-2 border-indigo-100/50 rounded-[2rem] cursor-pointer hover:bg-indigo-50 transition-all group"
                onClick={(e) => {
                    e.preventDefault();
                    setSyncToAll(!syncToAll);
                }}
            >
                <div className={`w-12 h-12 rounded-2xl flex items-center justify-center transition-all shadow-lg ${syncToAll ? 'bg-indigo-600 text-white shadow-indigo-900/20' : 'bg-white text-slate-300 shadow-slate-900/5'}`}>
                    {syncToAll ? <CheckIcon className="w-6 h-6" /> : <GlobeAltIcon className="w-6 h-6 group-hover:text-indigo-400" />}
                </div>
                <div className="flex-1">
                    <p className="text-[10px] font-black text-[#18216D] uppercase tracking-widest leading-none mb-1">
                        Sync to All Streams
                    </p>
                    <p className="text-[11px] text-slate-400 font-bold italic leading-tight">
                        Automatically propagate this change across all your assigned classes.
                    </p>
                </div>
                <div className={`w-12 h-7 rounded-full p-1 transition-all flex items-center ${syncToAll ? 'bg-indigo-600' : 'bg-slate-200'}`}>
                    <div className={`w-5 h-5 bg-white rounded-full shadow-sm transform transition-all ${syncToAll ? 'translate-x-5' : 'translate-x-0'}`} />
                </div>
            </label>
        </div>
    );
};

const OutcomeCard = ({ outcome, onDelete, onUpdate, isTeacher, area }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [syncToAll, setSyncToAll] = useState(true);
    const [editData, setEditData] = useState({
        description: outcome.description,
        suggested_activities: outcome.suggested_activities || '',
        code: outcome.code
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            const payload = {
                ...editData,
                sync_to_all_streams: (isTeacher && !area?.stream) ? syncToAll : false
            };
            await onUpdate(outcome.id, payload);
            setIsEditing(false);
        } catch (err) {
            // Error handled by parent toast
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) handleSave();
        if (e.key === 'Escape') {
            setIsEditing(false);
            setEditData({
                description: outcome.description,
                suggested_activities: outcome.suggested_activities || '',
                code: outcome.code
            });
        }
    };

    return (
        <div className={`p-6 rounded-[2rem] border transition-all duration-300 group ${isEditing ? 'bg-white border-[#18216D]/30 shadow-2xl shadow-indigo-900/10' : 'bg-white border-slate-100 hover:border-[#18216D]/20 hover:shadow-xl hover:shadow-indigo-900/5'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    <div className="flex items-center gap-2 mb-3">
                        {isEditing ? (
                            <input
                                value={editData.code}
                                onChange={(e) => setEditData({ ...editData, code: e.target.value })}
                                className="text-[9px] font-black px-2.5 py-1 bg-white border border-[#18216D]/10 text-[#18216D] rounded-lg uppercase tracking-widest outline-none focus:ring-2 focus:ring-indigo-100"
                                placeholder="CODE"
                            />
                        ) : (
                            <span className="text-[9px] font-black px-2.5 py-1 bg-indigo-50 text-indigo-600 rounded-lg uppercase tracking-widest border border-indigo-100">
                                {outcome.code}
                            </span>
                        )}
                    </div>

                    {isEditing ? (
                        <div className="space-y-4">
                            <textarea
                                autoFocus
                                value={editData.description}
                                onChange={(e) => {
                                    setEditData({ ...editData, description: e.target.value });
                                    e.target.style.height = 'auto';
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                onFocus={(e) => {
                                    e.target.style.height = 'auto';
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                onKeyDown={handleKeyDown}
                                className="w-full p-4 bg-slate-50/50 border-2 border-transparent focus:border-indigo-100 focus:bg-white rounded-2xl transition-all font-bold text-[#18216D] outline-none resize-none Montserrat-Medium text-sm leading-relaxed italic min-h-[8rem] overflow-hidden"
                                placeholder="Outcome description..."
                            />
                            <SyncToggle
                                syncToAll={syncToAll}
                                setSyncToAll={setSyncToAll}
                                isVisible={isTeacher && !area?.stream}
                            />
                        </div>
                    ) : (
                        <>
                            <p className="text-sm font-bold text-slate-800 leading-relaxed italic Montserrat-Italic">"{outcome.description}"</p>
                        </>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={handleSave}
                                disabled={saving}
                                className="w-9 h-9 rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 flex items-center justify-center transition-all shadow-lg shadow-emerald-900/10"
                                title="Save (Ctrl+Enter)"
                            >
                                {saving ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <CheckIcon className="w-4 h-4" />}
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="w-9 h-9 rounded-xl bg-slate-100 text-slate-400 hover:bg-slate-200 flex items-center justify-center transition-all"
                                title="Cancel (Esc)"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col gap-2 md:opacity-60 group-hover:opacity-100 transition-opacity">
                            <button
                                onClick={() => setIsEditing(true)}
                                className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-[#18216D]/10 hover:text-[#18216D] hover:border-[#18216D]/20 flex items-center justify-center transition-all shadow-sm"
                            >
                                <i className="fas fa-edit text-[11px]"></i>
                            </button>
                            <button
                                onClick={() => onDelete(outcome)}
                                className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 flex items-center justify-center transition-all shadow-sm"
                            >
                                <i className="fas fa-trash-alt text-[11px]"></i>
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const ExperienceCard = ({ experience, onDelete, onUpdate, isTeacher, area, subStrandId }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editData, setEditData] = useState({
        description: experience.description,
        order: experience.order
    });

    const handleSave = async () => {
        setSaving(true);
        try {
            await onUpdate(experience.id, {
                ...editData,
                learning_area: area?.id
            });
            setIsEditing(false);
        } catch (err) {
            // Error handled by parent
        } finally {
            setSaving(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && e.ctrlKey) handleSave();
        if (e.key === 'Escape') {
            setIsEditing(false);
            setEditData({
                description: experience.description,
                order: experience.order
            });
        }
    };

    return (
        <div className={`p-5 rounded-2xl border transition-all duration-300 group ${isEditing ? 'bg-white border-[#18216D]/30 shadow-xl' : 'bg-white border-slate-100 hover:border-[#FFC425]/30'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <textarea
                            autoFocus
                            value={editData.description}
                            onChange={(e) => {
                                setEditData({ ...editData, description: e.target.value });
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            onKeyDown={handleKeyDown}
                            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-indigo-100 focus:bg-white rounded-xl transition-all font-medium text-slate-700 outline-none resize-none text-sm leading-relaxed italic min-h-[6rem] overflow-hidden"
                        />
                    ) : (
                        <p className="text-sm font-medium text-slate-700 leading-relaxed italic">
                            "{experience.description}"
                        </p>
                    )}
                </div>

                <div className="flex flex-col gap-2">
                    {isEditing ? (
                        <>
                            <button 
                                onClick={handleSave} 
                                disabled={saving}
                                className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-all shadow-lg shadow-emerald-900/10"
                            >
                                {saving ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <CheckIcon className="w-4 h-4 stroke-[3]" />}
                            </button>
                            <button
                                onClick={() => setIsEditing(false)}
                                className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 hover:bg-slate-200 flex items-center justify-center transition-all"
                            >
                                <XMarkIcon className="w-4 h-4" />
                            </button>
                        </>
                    ) : (
                        <div className="flex flex-col gap-2 md:opacity-60 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setIsEditing(true)} className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-[#18216D]/10 hover:text-[#18216D] hover:border-[#18216D]/20 flex items-center justify-center transition-all shadow-sm"><i className="fas fa-edit text-[11px]"></i></button>
                            <button onClick={() => onDelete(experience)} className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 flex items-center justify-center transition-all shadow-sm"><i className="fas fa-trash-alt text-[11px]"></i></button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const InquiryQuestionCard = ({ item, onDelete, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editValue, setEditValue] = useState(item.question);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onUpdate(item.id, { question: editValue });
            setIsEditing(false);
        } catch (err) { /* handled by parent */ }
        finally { setSaving(false); }
    };

    return (
        <div className={`p-5 rounded-2xl border transition-all duration-300 group ${isEditing ? 'bg-white border-[#18216D]/30 shadow-xl' : 'bg-white border-slate-100 hover:border-amber-200/50'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <textarea
                            autoFocus
                            value={editValue}
                            onChange={(e) => {
                                setEditValue(e.target.value);
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            onFocus={(e) => {
                                e.target.style.height = 'auto';
                                e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleSave(); if (e.key === 'Escape') setIsEditing(false); }}
                            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-amber-200 focus:bg-white rounded-xl transition-all font-medium text-slate-700 outline-none resize-none text-sm leading-relaxed italic min-h-[6rem] overflow-hidden"
                        />
                    ) : (
                        <p className="text-sm font-medium text-slate-700 leading-relaxed italic">&#34;{item.question}&#34;</p>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} disabled={saving} className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-all shadow-lg">
                                {saving ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <CheckIcon className="w-4 h-4 stroke-[3]" />}
                            </button>
                            <button onClick={() => setIsEditing(false)} className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 hover:bg-slate-200 flex items-center justify-center transition-all"><XMarkIcon className="w-4 h-4" /></button>
                        </>
                    ) : (
                        <div className="flex flex-col gap-2 md:opacity-60 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => setIsEditing(true)} className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-[#18216D]/10 hover:text-[#18216D] hover:border-[#18216D]/20 flex items-center justify-center transition-all shadow-sm"><i className="fas fa-edit text-[11px]"></i></button>
                            <button onClick={() => onDelete(item)} className="w-9 h-9 rounded-full bg-slate-50 border border-slate-100 text-slate-500 hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 flex items-center justify-center transition-all shadow-sm"><i className="fas fa-trash-alt text-[11px]"></i></button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const AssessmentMethodCard = ({ item, onDelete, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editValue, setEditValue] = useState(item.name);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onUpdate(item.id, { name: editValue });
            setIsEditing(false);
        } catch (err) { /* handled by parent */ }
        finally { setSaving(false); }
    };

    return (
        <div className={`p-5 rounded-2xl border transition-all duration-300 group ${isEditing ? 'bg-white border-[#18216D]/30 shadow-xl' : 'bg-white border-slate-100 hover:border-rose-200/50'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <input
                            autoFocus
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setIsEditing(false); }}
                            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-rose-200 focus:bg-white rounded-xl transition-all font-medium text-slate-700 outline-none text-sm"
                        />
                    ) : (
                        <p className="text-sm font-medium text-slate-700 leading-relaxed">{item.name}</p>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} disabled={saving} className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-all shadow-lg">
                                {saving ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <CheckIcon className="w-4 h-4 stroke-[3]" />}
                            </button>
                            <button onClick={() => setIsEditing(false)} className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 hover:bg-slate-200 flex items-center justify-center transition-all"><XMarkIcon className="w-4 h-4" /></button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setIsEditing(true)} className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"><i className="fas fa-edit text-[10px]"></i></button>
                            <button onClick={() => onDelete(item)} className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"><i className="fas fa-trash-alt text-[10px]"></i></button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const ResourceCard = ({ item, onDelete, onUpdate }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [editValue, setEditValue] = useState(item.name);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onUpdate(item.id, { name: editValue });
            setIsEditing(false);
        } catch (err) { /* handled by parent */ }
        finally { setSaving(false); }
    };

    return (
        <div className={`p-5 rounded-2xl border transition-all duration-300 group ${isEditing ? 'bg-white border-[#18216D]/30 shadow-xl' : 'bg-white border-slate-100 hover:border-emerald-200/50'}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {isEditing ? (
                        <input
                            autoFocus
                            value={editValue}
                            onChange={e => setEditValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') setIsEditing(false); }}
                            className="w-full p-4 bg-slate-50 border-2 border-transparent focus:border-emerald-200 focus:bg-white rounded-xl transition-all font-medium text-slate-700 outline-none text-sm"
                        />
                    ) : (
                        <p className="text-sm font-medium text-slate-700 leading-relaxed">{item.name}</p>
                    )}
                </div>
                <div className="flex flex-col gap-2">
                    {isEditing ? (
                        <>
                            <button onClick={handleSave} disabled={saving} className="w-8 h-8 rounded-lg bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition-all shadow-lg">
                                {saving ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <CheckIcon className="w-4 h-4 stroke-[3]" />}
                            </button>
                            <button onClick={() => setIsEditing(false)} className="w-8 h-8 rounded-lg bg-slate-100 text-slate-400 hover:bg-slate-200 flex items-center justify-center transition-all"><XMarkIcon className="w-4 h-4" /></button>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setIsEditing(true)} className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 hover:text-emerald-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"><i className="fas fa-edit text-[10px]"></i></button>
                            <button onClick={() => onDelete(item)} className="w-8 h-8 rounded-lg bg-slate-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all opacity-0 group-hover:opacity-100"><i className="fas fa-trash-alt text-[10px]"></i></button>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const StrandAccordion = ({ strand, isExpanded, onToggle, onEditStrand, onDeleteStrand, onAddSubStrand, onEditSubStrand, onDeleteSubStrand, onAddOutcome, onUpdateOutcome, onDeleteOutcome, onAddExperience, onUpdateExperience, onDeleteExperience, onAddInquiryQuestion, onUpdateInquiryQuestion, onDeleteInquiryQuestion, onAddAssessmentMethod, onUpdateAssessmentMethod, onDeleteAssessmentMethod, onAddResource, onUpdateResource, onDeleteResource, isTeacher, area }) => {
    const [activeTabs, setActiveTabs] = useState({});

    const toggleTab = (subId, tab) => {
        setActiveTabs(prev => ({ ...prev, [subId]: tab }));
    };

    // Dynamic add label/action based on active tab
    const getAddConfig = (sub, currentTab) => {
        const map = {
            outcomes:    { label: 'Outcome',            action: () => onAddOutcome(sub.id) },
            experiences: { label: 'Experience',         action: () => onAddExperience(sub.id) },
            questions:   { label: 'Inquiry Question',   action: () => onAddInquiryQuestion(sub.id) },
            assessments: { label: 'Assessment Method',  action: () => onAddAssessmentMethod(sub.id) },
            resources:   { label: 'Resource',           action: () => onAddResource(sub.id) },
        };
        return map[currentTab] || map.outcomes;
    };

    return (
        <div className={`rounded-[2.5rem] border transition-all duration-500 overflow-hidden ${isExpanded ? 'border-[#18216D]/10 bg-white shadow-2xl shadow-indigo-900/5' : 'border-slate-100 bg-white/50 hover:bg-white'}`}>
            <div onClick={onToggle} className={`p-8 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-6 group transition-colors ${isExpanded ? 'bg-slate-50/50' : ''}`}>
                <div className="flex items-center gap-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-lg ${isExpanded ? 'bg-[#FFC425] -rotate-6' : 'bg-slate-100 group-hover:bg-[#18216D]'}`}>
                        <i className={`fas fa-layer-group text-lg transition-colors ${isExpanded ? 'text-[#18216D]' : 'text-slate-400 group-hover:text-white'}`}></i>
                    </div>
                    <div>
                        <h3 className={`text-xl font-black uppercase tracking-tighter transition-colors ${isExpanded ? 'text-[#18216D]' : 'text-slate-600'}`}>{strand.name}</h3>
                        <div className="flex items-center gap-3 mt-1.5">
                            <span className="text-[9px] font-black uppercase tracking-[0.2em] text-[#FFC425]">{strand.code}</span>
                            <span className="text-slate-200">|</span>
                            <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                                {strand.sub_strands?.length || 0} Sub-strands
                            </span>
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-4">
                    {isExpanded && (
                        <div className="flex items-center gap-2">
                            <button onClick={(e) => { e.stopPropagation(); onEditStrand(strand); }} className="px-5 py-2.5 bg-white border border-slate-200 text-slate-400 rounded-xl text-[10px] font-black uppercase tracking-widest hover:text-[#18216D] transition-all">Edit</button>
                            <button onClick={(e) => { e.stopPropagation(); onDeleteStrand(strand); }} className="w-10 h-10 bg-white border border-rose-100 text-rose-400 rounded-xl flex items-center justify-center hover:bg-rose-600 hover:text-white transition-all"><i className="fas fa-trash-alt text-[10px]"></i></button>
                        </div>
                    )}
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${isExpanded ? 'bg-[#18216D] text-white' : 'bg-slate-50 text-slate-300'}`}>
                        {isExpanded ? <ChevronUpIcon className="w-5 h-5" /> : <ChevronDownIcon className="w-5 h-5" />}
                    </div>
                </div>
            </div>

            {isExpanded && (
                <div className="px-8 pb-10 space-y-12 animate-in slide-in-from-top-4 duration-500 border-t border-slate-50">
                    <div className="pt-6 flex items-center justify-between">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em]">Curriculum Sub-strands</h4>
                        <button onClick={() => onAddSubStrand(strand.id)} className="px-6 py-3 bg-indigo-600 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-900/10">
                            <PlusIcon className="w-4 h-4" /> Add Sub-strand
                        </button>
                    </div>

                    <div className="space-y-16">
                        {strand.sub_strands?.map(sub => {
                            const currentTab = activeTabs[sub.id] || 'outcomes';
                            return (
                                <div key={sub.id} className="space-y-8 pl-4 border-l-2 border-[#FFC425]/20">
                                    <div className="flex flex-col gap-6">
                                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                            <h5 className="text-lg font-black text-[#18216D] uppercase tracking-tight italic">{sub.name}</h5>
                                            <div className="flex items-center gap-2">
                                                <button onClick={() => onEditSubStrand(sub)} className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 hover:text-[#18216D] flex items-center justify-center transition-all"><i className="fas fa-edit text-xs"></i></button>
                                                <button onClick={() => onDeleteSubStrand(sub)} className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 hover:text-rose-600 flex items-center justify-center transition-all"><i className="fas fa-trash-alt text-xs"></i></button>
                                                {(() => {
                                                    const { label, action } = getAddConfig(sub, currentTab);
                                                    return (
                                                        <button 
                                                            onClick={action}
                                                            className="px-5 py-2.5 bg-indigo-50 text-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 hover:bg-indigo-600 hover:text-white"
                                                        >
                                                            <PlusIcon className="w-4 h-4 stroke-[3]" /> Add {label}
                                                        </button>
                                                    );
                                                })()}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-6 border-b border-slate-200 w-full flex-wrap pb-0">
                                            <button 
                                                onClick={() => toggleTab(sub.id, 'outcomes')}
                                                className={`pb-3 pt-2 text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border-b-[3px] -mb-[1.5px] whitespace-nowrap ${currentTab === 'outcomes' ? 'border-[#18216D] text-[#18216D]' : 'border-transparent text-slate-400 hover:text-[#18216D]'}`}
                                            >
                                                Outcomes 
                                                <span className={`px-1.5 py-0.5 rounded-md text-[8px] ${currentTab === 'outcomes' ? 'bg-indigo-50 text-[#18216D]' : 'bg-slate-100 text-slate-500'}`}>
                                                    {sub.learning_outcomes?.length || 0}
                                                </span>
                                            </button>
                                            <button 
                                                onClick={() => toggleTab(sub.id, 'experiences')}
                                                className={`pb-3 pt-2 text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border-b-[3px] -mb-[1.5px] whitespace-nowrap ${currentTab === 'experiences' ? 'border-[#18216D] text-[#18216D]' : 'border-transparent text-slate-400 hover:text-[#18216D]'}`}
                                            >
                                                Experiences
                                                <span className={`px-1.5 py-0.5 rounded-md text-[8px] ${currentTab === 'experiences' ? 'bg-indigo-50 text-[#18216D]' : 'bg-slate-100 text-slate-500'}`}>
                                                    {sub.registry_experiences?.length || 0}
                                                </span>
                                            </button>
                                            <button 
                                                onClick={() => toggleTab(sub.id, 'questions')}
                                                className={`pb-3 pt-2 text-[10px] font-black uppercase tracking-widest transition-all flex items-center gap-2 border-b-[3px] -mb-[1.5px] whitespace-nowrap ${currentTab === 'questions' ? 'border-[#18216D] text-[#18216D]' : 'border-transparent text-slate-400 hover:text-[#18216D]'}`}
                                            >
                                                Enquiry
                                                <span className={`px-1.5 py-0.5 rounded-md text-[8px] ${currentTab === 'questions' ? 'bg-indigo-50 text-[#18216D]' : 'bg-slate-100 text-slate-500'}`}>
                                                    {sub.registry_questions?.length || 0}
                                                </span>
                                            </button>
                                        </div>
                                    </div>

                                    {currentTab === 'outcomes' ? (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-in fade-in slide-in-from-left-2 duration-300">
                                            {sub.learning_outcomes?.map(outcome => (
                                                <OutcomeCard key={outcome.id} outcome={outcome} onUpdate={onUpdateOutcome} onDelete={onDeleteOutcome} isTeacher={isTeacher} area={area} />
                                            ))}
                                            {(!sub.learning_outcomes || sub.learning_outcomes.length === 0) && (
                                                <div className="col-span-full py-12 border-2 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center text-slate-300">
                                                    <i className="fas fa-bullseye text-3xl mb-4"></i>
                                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">No Outcomes Defined</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : currentTab === 'experiences' ? (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in slide-in-from-right-2 duration-300">
                                            {sub.registry_experiences?.map(exp => (
                                                <ExperienceCard key={exp.id} experience={exp} onUpdate={onUpdateExperience} onDelete={onDeleteExperience} isTeacher={isTeacher} area={area} subStrandId={sub.id} />
                                            ))}
                                            {(!sub.registry_experiences || sub.registry_experiences.length === 0) && (
                                                <div className="col-span-full py-12 border-2 border-dashed border-slate-100 rounded-[2rem] flex flex-col items-center justify-center text-slate-300">
                                                    <i className="fas fa-walking text-3xl mb-4"></i>
                                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">No Experiences Defined</span>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 animate-in fade-in slide-in-from-right-2 duration-300">
                                            {sub.registry_questions?.map(item => (
                                                <InquiryQuestionCard key={item.id} item={item} onUpdate={onUpdateInquiryQuestion} onDelete={onDeleteInquiryQuestion} />
                                            ))}
                                            {(!sub.registry_questions || sub.registry_questions.length === 0) && (
                                                <div className="col-span-full py-12 border-2 border-dashed border-amber-50 rounded-[2rem] flex flex-col items-center justify-center text-slate-300">
                                                    <i className="fas fa-question-circle text-3xl mb-4"></i>
                                                    <span className="text-[10px] font-black uppercase tracking-[0.2em]">No Inquiry Questions Defined</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const CurriculumAreaManager = () => {
    const { areaId } = useParams();
    const navigate = useNavigate();
    const { get, post, patch, delete: del } = useApi();
    const { showToast } = useAppState();
    const [area, setArea] = useState(null);

    const counts = useMemo(() => {
        if (!area || !area.strands) return { strands: 0, subStrands: 0, outcomes: 0 };
        const strands = area.strands;
        let ssCount = 0;
        let loCount = 0;
        strands.forEach(s => {
            ssCount += (s.sub_strands?.length || 0);
            s.sub_strands?.forEach(ss => {
                loCount += (ss.learning_outcomes?.length || 0);
            });
        });
        return { strands: strands.length, subStrands: ssCount, outcomes: loCount };
    }, [area]);

    const queryClient = useQueryClient();

    const [expandedStrandId, setExpandedStrandId] = useState(null);
    const [editor, setEditor] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [syncToAll, setSyncToAll] = useState(true);

    const { data: profile } = useQuery({
        queryKey: ['user-profile'],
        queryFn: () => get('/api/auth/profile/'),
    });

    const isTeacher = !!profile?.teacher_id;

    const { data: areaData, isLoading: loadingArea } = useQuery({
        queryKey: ['learning-area-detail', areaId],
        queryFn: () => get(`/api/cbc/learning-areas/${areaId}/`),
        staleTime: 5 * 60 * 1000,   // 5 min — avoid re-fetch on every navigation
        gcTime:    10 * 60 * 1000,  // keep in cache 10 min
    });



    useEffect(() => { if (areaData) setArea(areaData); }, [areaData]);

    const strands = useMemo(() => area?.strands || [], [area]);

    const handleSave = async (e) => {
        e.preventDefault();
        setSaving(true);
        const formData = new FormData(e.target);
        const payload = Object.fromEntries(formData.entries());

        if (isTeacher && !area?.stream) {
            payload.sync_to_all_streams = syncToAll;
        }

        try {
            const typeMap = { 
                'strand': 'strands', 
                'sub-strand': 'sub-strands', 
                'outcome': 'learning-outcomes',
                'experience': 'learning-experiences',
                'inquiry-question': 'inquiry-questions',
                'assessment-method': 'assessment-methods',
                'resource': 'learning-resources'
            };
            const endpoint = `/api/cbc/${typeMap[editor.type]}/` + (editor.mode === 'edit' ? `${editor.data.id}/` : '');
            const method = editor.mode === 'edit' ? patch : post;

            if (editor.mode === 'create') {
                if (editor.type === 'strand') payload.learning_area = areaId;
                if (editor.type === 'sub-strand') payload.strand = editor.parentId;
                if (editor.type === 'outcome') payload.sub_strand = editor.parentId;
                if (editor.type === 'experience') {
                    payload.sub_strand = editor.parentId;
                    payload.learning_area = areaId;
                }
                if (editor.type === 'inquiry-question') payload.sub_strand = editor.parentId;
                if (editor.type === 'assessment-method') payload.sub_strand = editor.parentId;
                if (editor.type === 'resource') payload.sub_strand = editor.parentId;
            }

            await method(endpoint, payload);
            showToast(`${editor.type} saved`, 'success');
            setEditor(null);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
        } catch (err) {
            const errorData = err.response?.data;
            let errorMessage = err.message;
            if (errorData) {
                if (typeof errorData === 'string') errorMessage = errorData;
                else if (errorData.detail) errorMessage = errorData.detail;
                else if (errorData.non_field_errors) errorMessage = errorData.non_field_errors[0];
                else {
                    const firstKey = Object.keys(errorData)[0];
                    errorMessage = `${firstKey}: ${Array.isArray(errorData[firstKey]) ? errorData[firstKey][0] : errorData[firstKey]}`;
                }
            }
            showToast(`Error: ${errorMessage}`, 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdateOutcome = async (id, data) => {
        try {
            await patch(`/api/cbc/learning-outcomes/${id}/`, data);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
            showToast('Outcome updated successfully', 'success');
        } catch (err) {
            const errorData = err.response?.data;
            showToast(`Update failed: ${errorData?.detail || errorData?.non_field_errors?.[0] || err.message}`, 'error');
            throw err;
        }
    };

    const handleUpdateExperience = async (id, data) => {
        try {
            await patch(`/api/cbc/learning-experiences/${id}/`, data);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
            showToast('Experience updated successfully', 'success');
        } catch (err) {
            const errorData = err.response?.data;
            showToast(`Update failed: ${errorData?.detail || errorData?.non_field_errors?.[0] || err.message}`, 'error');
            throw err;
        }
    };

    const handleUpdateInquiryQuestion = async (id, data) => {
        try {
            await patch(`/api/cbc/inquiry-questions/${id}/`, data);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
            showToast('Inquiry question updated', 'success');
        } catch (err) {
            const errorData = err.response?.data;
            showToast(`Update failed: ${errorData?.detail || err.message}`, 'error');
            throw err;
        }
    };

    const handleUpdateAssessmentMethod = async (id, data) => {
        try {
            await patch(`/api/cbc/assessment-methods/${id}/`, data);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
            showToast('Assessment method updated', 'success');
        } catch (err) {
            const errorData = err.response?.data;
            showToast(`Update failed: ${errorData?.detail || err.message}`, 'error');
            throw err;
        }
    };

    const handleUpdateResource = async (id, data) => {
        try {
            await patch(`/api/cbc/learning-resources/${id}/`, data);
            queryClient.invalidateQueries(['learning-area-detail', areaId]);
            showToast('Resource updated', 'success');
        } catch (err) {
            const errorData = err.response?.data;
            showToast(`Update failed: ${errorData?.detail || err.message}`, 'error');
            throw err;
        }
    };

    if (loadingArea) return <div className="flex items-center justify-center py-40 animate-pulse text-[10px] font-black text-slate-400">LOADING CURRICULUM...</div>;

    return (
        <div className="max-w-7xl mx-auto space-y-12 pb-20 animate-in fade-in duration-1000">
            <div className="bg-white border-b border-slate-100 shadow-sm -mx-6 px-6 py-4">
                <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2.5 hover:bg-slate-50 rounded-xl text-slate-400 hover:text-[#18216D] transition-all border border-transparent hover:border-slate-100"
                        >
                            <ChevronLeftIcon className="w-5 h-5" />
                        </button>
                        <div>
                            <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em]">Master Registry</span>
                                <div className="h-1 w-1 bg-slate-200 rounded-full" />
                                <span className="text-[9px] font-black text-indigo-500 uppercase tracking-widest">{area?.grade_level_name}</span>
                            </div>
                            <h1 className="text-xl font-black text-[#18216D] tracking-tight flex items-center gap-3">
                                {area?.name}
                                <span className="px-2 py-0.5 bg-[#FFC425]/10 text-[#18216D] text-[9px] font-black uppercase tracking-widest rounded-lg border border-[#FFC425]/20">
                                    {area?.code}
                                </span>
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="hidden sm:flex items-center gap-2">
                            <div className="bg-[#18216D]/5 px-3 py-1.5 rounded-xl border border-[#18216D]/10 flex items-center gap-2">
                                <span className="text-[10px] font-black text-[#18216D]">{counts.strands}</span>
                                <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest">Strands</span>
                            </div>
                            <div className="bg-indigo-50 px-3 py-1.5 rounded-xl border border-indigo-100 flex items-center gap-2">
                                <span className="text-[10px] font-black text-indigo-600">{counts.subStrands}</span>
                                <span className="text-[8px] font-black text-indigo-400 uppercase tracking-widest">Topics</span>
                            </div>
                            <div className="bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100 flex items-center gap-2">
                                <span className="text-[10px] font-black text-emerald-600">{counts.outcomes}</span>
                                <span className="text-[8px] font-black text-emerald-400 uppercase tracking-widest">Outcomes</span>
                            </div>
                        </div>

                        <div className="h-6 w-px bg-slate-100 mx-2 hidden md:block" />

                        <button
                            onClick={() => setEditor({ type: 'strand', mode: 'create', parentId: areaId })}
                            className="px-6 py-3 bg-[#18216D] text-white rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-[#0D164F] transition-all shadow-xl shadow-indigo-900/20 flex items-center space-x-2 active:scale-95 group"
                        >
                            <PlusIcon className="w-4 h-4 stroke-[3] group-hover:rotate-90 transition-transform" />
                            <span>Add New Strand</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="space-y-6">

                {strands.map(strand => (
                    <StrandAccordion
                        key={strand.id}
                        strand={strand}
                        isExpanded={expandedStrandId === strand.id}
                        onToggle={() => setExpandedStrandId(expandedStrandId === strand.id ? null : strand.id)}
                        onEditStrand={(s) => setEditor({ type: 'strand', mode: 'edit', data: s })}
                        onDeleteStrand={(s) => setDeleteConfirm({ type: 'strand', id: s.id, name: s.name })}
                        onAddSubStrand={(id) => setEditor({ type: 'sub-strand', mode: 'create', parentId: id })}
                        onEditSubStrand={(ss) => setEditor({ type: 'sub-strand', mode: 'edit', data: ss })}
                        onDeleteSubStrand={(ss) => setDeleteConfirm({ type: 'sub-strand', id: ss.id, name: ss.name })}
                        onAddOutcome={(id) => setEditor({ type: 'outcome', mode: 'create', parentId: id })}
                        onUpdateOutcome={handleUpdateOutcome}
                        onDeleteOutcome={(o) => setDeleteConfirm({ type: 'outcome', id: o.id, name: o.description })}
                        onAddExperience={(id) => setEditor({ type: 'experience', mode: 'create', parentId: id })}
                        onUpdateExperience={handleUpdateExperience}
                        onDeleteExperience={(e) => setDeleteConfirm({ type: 'experience', id: e.id, name: e.description })}
                        onAddInquiryQuestion={(id) => setEditor({ type: 'inquiry-question', mode: 'create', parentId: id })}
                        onUpdateInquiryQuestion={handleUpdateInquiryQuestion}
                        onDeleteInquiryQuestion={(item) => setDeleteConfirm({ type: 'inquiry-question', id: item.id, name: item.question })}
                        onAddAssessmentMethod={(id) => setEditor({ type: 'assessment-method', mode: 'create', parentId: id })}
                        onUpdateAssessmentMethod={handleUpdateAssessmentMethod}
                        onDeleteAssessmentMethod={(item) => setDeleteConfirm({ type: 'assessment-method', id: item.id, name: item.name })}
                        onAddResource={(id) => setEditor({ type: 'resource', mode: 'create', parentId: id })}
                        onUpdateResource={handleUpdateResource}
                        onDeleteResource={(item) => setDeleteConfirm({ type: 'resource', id: item.id, name: item.name })}
                        isTeacher={isTeacher}
                        area={area}
                    />
                ))}
            </div>

            {editor && (() => {
                let defaultCode = editor.data?.code || '';
                let defaultOrder = editor.data?.order || 1;
                
                if (editor.mode === 'create') {
                    if (editor.type === 'strand') {
                        defaultOrder = (area?.strands?.length || 0) + 1;
                        defaultCode = `${area?.code || 'XX'}-S${defaultOrder.toString().padStart(2, '0')}`;
                    } else if (editor.type === 'sub-strand') {
                        const strand = area?.strands?.find(s => s.id === editor.parentId);
                        if (strand) {
                            defaultOrder = (strand.sub_strands?.length || 0) + 1;
                            defaultCode = `${strand.code}-SS${defaultOrder.toString().padStart(2, '0')}`;
                        }
                    } else if (['outcome', 'experience', 'inquiry-question', 'assessment-method', 'resource'].includes(editor.type)) {
                        let parentSubStrand = null;
                        area?.strands?.forEach(s => {
                            const found = s.sub_strands?.find(ss => ss.id === editor.parentId);
                            if (found) parentSubStrand = found;
                        });
                        if (parentSubStrand) {
                            if (editor.type === 'outcome') {
                                defaultOrder = (parentSubStrand.learning_outcomes?.length || 0) + 1;
                                defaultCode = `${parentSubStrand.code}-O${defaultOrder.toString().padStart(2, '0')}`;
                            } else if (editor.type === 'experience') {
                                defaultOrder = (parentSubStrand.registry_experiences?.length || 0) + 1;
                            } else if (editor.type === 'inquiry-question') {
                                defaultOrder = (parentSubStrand.registry_questions?.length || 0) + 1;
                            }
                        }
                    }
                }

                return (
                <Modal
                    isOpen={!!editor} onClose={() => setEditor(null)}
                    title={editor.mode === 'create' ? `New ${editor.type}` : `Edit ${editor.type}`}
                    variant="compact"
                    type={editor.type}
                    footer={
                        <div className="flex items-center justify-end gap-8 w-full mr-4">
                            <button
                                onClick={() => setEditor(null)}
                                className="text-[12px] font-black text-slate-400 hover:text-rose-500 uppercase tracking-widest transition-colors duration-300"
                            >
                                Discard
                            </button>
                            <button
                                form="editor-form"
                                type="submit"
                                disabled={saving}
                                className="px-10 py-5 bg-[#18216D] text-white rounded-2xl font-black text-[12px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-900/20 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 border-b-4 border-[#FFC425]"
                            >
                                {saving ? <i className="fas fa-spinner fa-spin"></i> : <CheckIcon className="w-4 h-4" />}
                                {saving ? 'UNIFYING...' : `PUBLISH ${editor.type.toUpperCase()}`}
                            </button>
                        </div>
                    }
                >
                    <form id="editor-form" onSubmit={handleSave} className="mt-2 space-y-10">
                        <div className="flex flex-col gap-8">
                            <div className="group space-y-4">
                                <div className="flex items-center gap-3">
                                    <div className="w-1.5 h-6 bg-[#FFC425] rounded-full"></div>
                                    <label className="text-[11px] font-black text-[#18216D] uppercase tracking-[0.3em]">{editor.type} Details</label>
                                </div>
                                <div className="relative">
                                    <div className="absolute left-6 top-6 text-[#18216D]/30 group-focus-within:text-[#FFC425] transition-colors">
                                        <Bars3BottomLeftIcon className="w-6 h-6" />
                                    </div>
                                    {editor.type === 'outcome' || editor.type === 'experience' ? (
                                        <textarea
                                            id="editor-main-input"
                                            name="description"
                                            defaultValue={editor.data?.description || ''}
                                            required
                                            onChange={(e) => {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            placeholder={`Enter precise ${editor.type} description...`}
                                            className="w-full pl-16 pr-8 py-6 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white focus:shadow-[0_0_20px_-5px_rgba(255,196,37,0.3)] rounded-[2rem] font-bold text-[#18216D] outline-none transition-all resize-none italic min-h-[8rem] overflow-hidden"
                                        />
                                    ) : editor.type === 'inquiry-question' ? (
                                        <textarea
                                            id="editor-main-input"
                                            name="question"
                                            defaultValue={editor.data?.question || ''}
                                            required
                                            onChange={(e) => {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            onFocus={(e) => {
                                                e.target.style.height = 'auto';
                                                e.target.style.height = `${e.target.scrollHeight}px`;
                                            }}
                                            placeholder="Enter the inquiry question..."
                                            className="w-full pl-16 pr-8 py-6 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white focus:shadow-[0_0_20px_-5px_rgba(255,196,37,0.3)] rounded-[2rem] font-bold text-[#18216D] outline-none transition-all resize-none italic min-h-[8rem] overflow-hidden"
                                        />
                                    ) : editor.type === 'assessment-method' || editor.type === 'resource' ? (
                                        <input
                                            id="editor-main-input"
                                            name="name"
                                            defaultValue={editor.data?.name || ''}
                                            required
                                            placeholder={editor.type === 'assessment-method' ? 'e.g. Portfolio Assessment' : 'e.g. Textbook, Charts'}
                                            className="w-full pl-16 pr-8 py-7 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white focus:shadow-[0_0_20px_-5px_rgba(255,196,37,0.3)] rounded-[2rem] font-black text-[#18216D] outline-none italic transition-all"
                                        />
                                    ) : (
                                        <input
                                            id="editor-main-input"
                                            name="name"
                                            defaultValue={editor.data?.name || ''}
                                            required
                                            placeholder={`e.g. ${editor.type === 'strand' ? 'Geometry' : 'Shapes'}`}
                                            className="w-full pl-16 pr-8 py-7 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white focus:shadow-[0_0_20px_-5px_rgba(255,196,37,0.3)] rounded-[2rem] font-black text-[#18216D] outline-none uppercase italic transition-all"
                                        />
                                    )}
                                </div>
                                

                            </div>

                            <div className="grid grid-cols-2 gap-8">
                                    {editor.type !== 'experience' && editor.type !== 'inquiry-question' && editor.type !== 'assessment-method' && editor.type !== 'resource' && (
                                        <div className="group space-y-4">
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Registry Code</label>
                                            <div className="relative">
                                                <div className="absolute left-6 top-1/2 -translate-y-1/2 text-[#18216D]/30 group-focus-within:text-[#FFC425] transition-colors">
                                                    <HashtagIcon className="w-5 h-5" />
                                                </div>
                                                <input
                                                    name="code"
                                                    defaultValue={defaultCode}
                                                    required
                                                    placeholder="CODE"
                                                    className="w-full pl-14 pr-8 py-5 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white rounded-2xl font-black text-[#18216D] uppercase outline-none transition-all"
                                                />
                                            </div>
                                        </div>
                                    )}

                                {!['inquiry-question', 'assessment-method', 'resource'].includes(editor.type) && (
                                <div className="group space-y-4">
                                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-4">Display Order</label>
                                    <div className="relative">
                                        <div className="absolute left-6 top-1/2 -translate-y-1/2 text-[#18216D]/30 group-focus-within:text-[#FFC425] transition-colors">
                                            <NumberedListIcon className="w-5 h-5" />
                                        </div>
                                        <input
                                            name="order"
                                            type="number"
                                            defaultValue={defaultOrder}
                                            className="w-full pl-14 pr-8 py-5 bg-slate-50 border-2 border-transparent focus:border-[#FFC425] focus:bg-white rounded-2xl font-black text-[#18216D] outline-none transition-all"
                                        />
                                    </div>
                                </div>
                                )}

                                <SyncToggle syncToAll={syncToAll} setSyncToAll={setSyncToAll} isVisible={isTeacher && !area?.stream} />
                            </div>
                        </div>
                    </form>
                </Modal>
                );
            })()}

            {deleteConfirm && (
                <Modal
                    isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} type="danger" title={`Delete ${deleteConfirm.type}`}
                    footer={
                        <div className="flex gap-4 w-full mt-4">
                            <button
                                onClick={() => setDeleteConfirm(null)}
                                className="flex-1 py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-slate-200 transition-all"
                            >
                                Cancel
                            </button>
                            <button
                                disabled={deleting}
                                onClick={async () => {
                                    setDeleting(true);
                                    try {
                                        const endpointMap = {
                                            'outcome': '/api/cbc/learning-outcomes/',
                                            'experience': '/api/cbc/learning-experiences/',
                                            'strand': '/api/cbc/strands/',
                                            'sub-strand': '/api/cbc/sub-strands/',
                                            'inquiry-question': '/api/cbc/inquiry-questions/',
                                            'assessment-method': '/api/cbc/assessment-methods/',
                                            'resource': '/api/cbc/learning-resources/'
                                        };
                                        const endpoint = endpointMap[deleteConfirm.type] + `${deleteConfirm.id}/?learning_area=${areaId}`;
                                        await del(endpoint);
                                        showToast(`${deleteConfirm.type} removed successfully`, 'success');
                                        setDeleteConfirm(null);
                                        queryClient.invalidateQueries(['learning-area-detail', areaId]);
                                    } catch (err) {
                                        showToast(`Deletion failed: ${err.response?.data?.detail || err.message}`, 'error');
                                    } finally {
                                        setDeleting(false);
                                    }
                                }}
                                className="flex-1 py-4 bg-rose-600 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-rose-900/20 hover:bg-rose-700 transition-all flex items-center justify-center gap-3 border-b-4 border-rose-800"
                            >
                                {deleting ? <i className="fas fa-spinner fa-spin"></i> : <XMarkIcon className="w-5 h-5" />}
                                Yes, Delete It
                            </button>
                        </div>
                    }
                >
                    <div className="p-4 space-y-6">
                        <div className="flex items-center gap-4 text-rose-600 bg-rose-50 p-5 rounded-2xl border border-rose-100">
                            <i className="fas fa-exclamation-triangle text-3xl"></i>
                            <p className="text-sm font-bold leading-snug">
                                Are you sure you want to remove this {deleteConfirm.type}?
                            </p>
                        </div>
                        <p className="text-sm font-medium text-slate-500 italic p-2 text-center bg-slate-50 rounded-xl">
                            "{deleteConfirm.name}"
                        </p>
                        <p className="text-[10px] font-black text-rose-400 uppercase tracking-[0.3em] mt-4 p-4 border border-dashed border-rose-100 rounded-xl text-center">
                            This action will remove it from your view.
                        </p>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default CurriculumAreaManager;
